import type { Column, ColumnDef, ColumnDefBase, ColumnHelper, Row, Table } from "@tanstack/vue-table"
import { getCoreRowModel, useVueTable } from "@tanstack/vue-table"
import { merge } from "lodash-es"

import type { SortByParams } from "@finq/app-base/lib/types/common"

import { UiTableFetcherClasses, UiTableFetcherProps } from "@finq/ui/components/ui/Table/Fetcher.interface"

export type TableVariant = "default" | "compact"

export interface UiTableBaseProps<T> {
  classes?: Partial<UiTableFetcherClasses>
  variant?: TableVariant
  rowLink?: TypedRouteProps | ((row: Row<T>) => TypedRouteProps)
  columns: ((colHelper: ColumnHelper<T>) => ColumnDefBase<T, any>[]) | ColumnDefBase<T, any>[]

  onRowClick?: (row: Row<T>) => void
  onRowMouseEnter?: (row: Row<T>) => void
}

export interface UiTableBasicProps<T> extends UiTableBaseProps<T> {
  data: T[]
  modelValue?: SortByParams
}

export function useTableCore<T>(table: Table<T>) {
  const rows = computed(() => table.getRowModel().rows)

  const hasFooter = computed(() => {
    return table
      .getFooterGroups()
      .some((footerGroup) => footerGroup.headers.some((footer) => footer.column.columnDef.footer))
  })

  return {
    rows,
    hasFooter,
  }
}

export const generateMetaStyles = (column: Column<any>) => {
  return {
    flex: column.columnDef.meta?.flex,
    width: column.columnDef.meta?.width,
  }
}

export const getRowLinkValue = (row: Row<any>, rowLink: UiTableBaseProps<any>["rowLink"]) => {
  if (!rowLink) return undefined
  return typeof rowLink === "function" ? rowLink?.(row) : rowLink
}

export const getTableClasses = (variant?: TableVariant, classes?: Partial<UiTableFetcherClasses>) => {
  switch (variant) {
    case "compact":
      return {
        ...classes,
        header: {
          ...classes?.header,
          row: ["min-md:min-h-12 border-none px-4 py-0", classes?.header?.row],
          cell: ["py-0 ps-0 last:pe-0", classes?.header?.cell],
        },
        footer: {
          ...classes?.footer,
          row: ["border-none px-4 py-3", classes?.footer?.row],
          cell: ["py-0 ps-0 last:pe-0", classes?.footer?.cell],
        },
        body: {
          ...classes?.body,
          row: ["min-md:min-h-12 z-0 border-none px-4 py-3 odd:bg-neutral-100", classes?.body?.row],
          cell: ["py-0 ps-0 last:pe-0", classes?.body?.cell],
        },
      }

    default:
      return classes
  }
}

export interface SkeletonTableProps<T> {
  loadingCount?: number
  columns: ColumnDef<T>[]
  classes?: Partial<UiTableFetcherClasses>
}

export function useSkeletonTable<T>(props: SkeletonTableProps<T>) {
  const loadingSkeletons = computed(() => {
    return Array.from({ length: props.loadingCount || 5 }).map(() => ({}))
  })

  const skeletonTable = useVueTable({
    get data() {
      return loadingSkeletons.value as T[]
    },
    get columns() {
      return props.columns
    },
    getCoreRowModel: getCoreRowModel(),
  })

  return { skeletonTable, loadingSkeletons }
}
